import { useState, useRef, useEffect, useMemo } from 'react';
import { useBedarfeList } from '../../api/bedarfe';
import {
  BedarfStatus,
  calculateTotalHours,
  formatHours,
  type Bedarf,
} from '../../types/bedarf';

interface BedarfSelectProps {
  value: string | null;
  onChange: (id: string | null, bedarf: Bedarf | null) => void;
  label?: string;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  id?: string;
}

/** A Stelle that is filled or lost is no longer being applied to. */
const CLOSED_STATUSES: BedarfStatus[] = [
  BedarfStatus.GEWONNEN,
  BedarfStatus.VERLOREN,
];

/** What the closed input shows — the name alone is enough to recognise a Stelle. */
const bedarfName = (bedarf: Bedarf): string =>
  [bedarf.childFirstName, bedarf.childLastName].filter(Boolean).join(' ');

/**
 * The line under the name: everything needed to tell two Stellen of the same
 * child apart — school, city and the hours the Stundenplan adds up to.
 */
const bedarfDetails = (bedarf: Bedarf): string => {
  const hours = calculateTotalHours(bedarf);
  return [
    bedarf.schoolName,
    bedarf.schoolCity?.name,
    hours > 0 ? `${formatHours(hours)}/Woche` : null,
  ]
    .filter(Boolean)
    .join(' · ');
};

/**
 * Single-select combobox over the open Bedarfe, modelled on StadtSelect. No
 * "create" affordance: a Stelle is created in the Bedarfe module, never in
 * passing from a Bewerber form.
 */
export function BedarfSelect({
  value,
  onChange,
  label,
  placeholder = '-- Keine Stelle --',
  error,
  disabled = false,
  id,
}: BedarfSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { data, isLoading } = useBedarfeList({ limit: 500 });
  const bedarfe = useMemo(() => data?.data ?? [], [data]);

  const selectedBedarf = useMemo(
    () => (value ? bedarfe.find((b) => b.id === value) ?? null : null),
    [bedarfe, value],
  );

  /**
   * Closed Bedarfe stay out of the list, but the one already assigned stays
   * selectable — otherwise saving an unrelated field would silently drop it.
   */
  const selectableBedarfe = useMemo(
    () =>
      bedarfe.filter(
        (b) => !CLOSED_STATUSES.includes(b.status) || b.id === value,
      ),
    [bedarfe, value],
  );

  const filteredBedarfe = useMemo(() => {
    const search = searchText.toLowerCase().trim();
    if (!search) return selectableBedarfe;
    return selectableBedarfe.filter((b) =>
      [bedarfName(b), b.schoolName, b.schoolCity?.name]
        .filter(Boolean)
        .some((field) => field!.toLowerCase().includes(search)),
    );
  }, [selectableBedarfe, searchText]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchText('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchText('');
    } else if (e.key === 'Enter' && !isOpen) {
      e.preventDefault();
      setIsOpen(true);
    }
  };

  const handleSelect = (bedarf: Bedarf) => {
    onChange(bedarf.id, bedarf);
    setIsOpen(false);
    setSearchText('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null, null);
    setSearchText('');
  };

  const inputClassName = `
    block w-full rounded-md shadow-sm sm:text-sm
    ${error
      ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
      : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
    }
    ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white cursor-pointer'}
  `;

  return (
    <div ref={containerRef} className="relative">
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}

      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          id={id}
          value={isOpen ? searchText : selectedBedarf ? bedarfName(selectedBedarf) : ''}
          onChange={(e) => setSearchText(e.target.value)}
          onFocus={() => !disabled && setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className={inputClassName}
          autoComplete="off"
        />

        {value && !isOpen && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute inset-y-0 right-8 flex items-center pr-2 text-gray-400 hover:text-gray-600"
            aria-label="Stelle entfernen"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}

        <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <svg
            className={`h-5 w-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}

      {/* The panel is wider than the input on purpose: this select often sits in
          a narrow column, where school, city and hours would wrap over three
          lines. */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full min-w-[22rem] bg-white rounded-md shadow-lg border border-gray-200 max-h-60 overflow-auto">
          {isLoading ? (
            <div className="px-4 py-3 text-sm text-gray-500">Laden...</div>
          ) : filteredBedarfe.length > 0 ? (
            <ul className="py-1">
              {filteredBedarfe.map((bedarf) => {
                const details = bedarfDetails(bedarf);
                return (
                  <li key={bedarf.id}>
                    <div
                      className={`
                        px-4 py-2 cursor-pointer
                        ${bedarf.id === value ? 'bg-primary-50 text-primary-700' : 'hover:bg-gray-50'}
                      `}
                      onClick={() => handleSelect(bedarf)}
                    >
                      <div className="text-sm font-medium">{bedarfName(bedarf)}</div>
                      {details && (
                        <div className="text-xs text-gray-500">{details}</div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="px-4 py-3 text-sm text-gray-500">Keine Stelle gefunden</div>
          )}
        </div>
      )}
    </div>
  );
}
